import { useState } from "react";
import { motion } from "motion/react";
import { Hammer, FileText, ArrowRight } from "lucide-react";
import { useJobPostProgressContext } from "../../../../context/jobPostProgressFrom";
import { Button } from "../../../../components/ui/button";
import { Card, CardContent } from "../../../../components/ui/card";
import { Textarea } from "../../../../components/ui/textarea";
import { StepFooterNav } from "../../shared/NavigationFooter";

export function Step3({ nextStep, preStep, currentStep, totalSteps }) {
    const { jobPostProgress, setSelectedService } = useJobPostProgressContext();
    const [errors, setErrors] = useState({});

    // --- equipment sets ---
    const snowEquipment = [
        {
            image:
                "https://cdn.builder.io/api/v1/image/assets/TEMP/a988a2bfbb1478f7ed53a8ec4fec2f24319ed2d636377504ac60f8bd2f6c97cb",
            title: "Environmental Option Shovel or Pusher",
            description: "",
        },
        {
            image:
                "https://cdn.builder.io/api/v1/image/assets/TEMP/c6c5770b118c781ee51420d1e8b7370a8503f23d927597e493a568572bbc153d",
            title: "Gas or Electric Snowblower",
            description: "",
        },
    ];

    const lawnEquipment = [
        {
            image:
                "https://cdn.builder.io/api/v1/image/assets/TEMP/e955745b9be149a9d1a5b681e6cf2cc079b496e3b7db16a23796c0a3d17867c1",
            title: "Environmental Reel or manual push mower",
            description: "",
        },
        {
            image:
                "https://cdn.builder.io/api/v1/image/assets/TEMP/f9d8092cf6fd41edc1ae58a049a6edc032cb1433142b3f6c1edd230ee3e1072b",
            title: "Electric Mower",
            description: "",
        },
        {
            image:
                "https://cdn.builder.io/api/v1/image/assets/TEMP/a24e6cecef1ca43aaaf68b3ad2668f3dba095a7fd07123f8612aa441c187f9e9",
            title: "Gas Mower",
            description: "",
        },
        {
            image:
                "https://cdn.builder.io/api/v1/image/assets/TEMP/4cbb697fc3a8320442abde9abf7b53bcdaa6c2d513e22ecf25b893070eddfe48",
            title: "Environmental Manual edge or rotary trimme",
            description: "r",
        },
        {
            image:
                "https://cdn.builder.io/api/v1/image/assets/TEMP/68c537078d107ec4c9f35ef8af594312da8e64939a6dbca9fe9927dbf5374171",
            title: "Electric String Trimmer",
            description: "",
        },
        {
            image:
                "https://cdn.builder.io/api/v1/image/assets/TEMP/68c537078d107ec4c9f35ef8af594312da8e64939a6dbca9fe9927dbf5374171",
            title: "Gas String Trimmer",
            description: "",
        },
    ];


    const leafRakingEquipment = [
        { image: "https://i.imgur.com/3OSMtre.png", title: "Leaf Rake", description: "" },
    ];

    let equipmentData = snowEquipment;
    if (jobPostProgress.selectedService.service === "Leaf Raking") {
        equipmentData = leafRakingEquipment;
    } else if (jobPostProgress.selectedService.service === "Lawn Mowing") {
        equipmentData = lawnEquipment;
    }

    // --- default instruction messages ---
    const defaultMessages = {
        "Snow Shoveling": {
            "Instructions": `Ex Shovels are inside the storm door, please leave them there after you are finished. Shovel the driveway, front stairs and walkway beside the house as well. Thanks!\n\nEx Snowblower is inside the garage (unlocked), hold the handle and pull the starter cord, use the shovels if it won’t start. Use the shovels to shovel the walkway leading up to the house. You can leave the shovels outside.\n`
        },
        "Lawn Mowing": {
            "Instructions": `Ex Reel mower is inside the shed in the backyard, please leave it there after you are finished. Mow the front lawn only. No need to trim the edges. Thanks!\n\nEx Electric lawn mower is inside the garage (unlocked), it’s already plugged in to the wall with an extension cord. When mowing the back lawn, it needs to be unplugged and plugged into the outlet beside the door in the backyard. Please mow the front and backyard.\n`
        },
        "Leaf Raking": {
            "Instructions": `ex. Rakes and bags are inside the storm door. Please return them there when you're done. Be sure to rake the front yard and backyard. If you fill any bags, leave them by the curb. Thanks!\n\nex. The rake and leaf bags are in the backyard shed (unlocked). Please rake the front and back yards and bag the leaves. If you run out of bags, stack the leaves neatly on the lawn near the door. You can leave the rake outside when you're done.`
        }
    };

    const getDefaultMessage = () => {
        const selectedService = jobPostProgress.selectedService.service;
        return defaultMessages[selectedService]?.Instructions || "";
    };

    // --- handlers ---
    const handleSelectEquipment = (equipmentFullName) => {
        const currentEquipment = jobPostProgress.selectedService.serviceEquipment;
        const updatedEquipment = currentEquipment.includes(equipmentFullName)
            ? currentEquipment.filter((item) => item !== equipmentFullName)
            : [...currentEquipment, equipmentFullName];

        setSelectedService(
            {
                service: jobPostProgress.selectedService.service,
                serviceEquipment: updatedEquipment,
                serviceInstruction: jobPostProgress.selectedService.serviceInstruction,
            },
            false
        );
    };

    const handleNext = () => {
        const newErrors = {};
        if (
            !jobPostProgress.selectedService.serviceEquipment ||
            jobPostProgress.selectedService.serviceEquipment.length === 0
        ) {
            newErrors.equipment = "Please select at least one equipment option.";
        }
        if (
            !jobPostProgress.selectedService.serviceInstruction ||
            jobPostProgress.selectedService.serviceInstruction.length === 0
        ) {
            newErrors.instructions = "Please provide specific instructions.";
        }
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        nextStep();
    };

    // --- render ---
    return (
        <div className="min-h-screen py-10">
            <div className="max-w-3xl mx-auto space-y-10">
                {/* Header */}
                <div className="text-center space-y-3">
                    <h1 className="text-3xl font-bold text-black">
                        Equipment & Instructions
                    </h1>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        Choose equipment and provide any specific instructions
                    </p>
                </div>

                {/* Equipment Options */}
                <div className="space-y-4">
                    <label className="flex items-center gap-2 text-lg font-medium">
                        <Hammer className="w-5 h-5" />
                        Equipment Provided
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {equipmentData.map((equipment, index) => {
                            const active = jobPostProgress.selectedService.serviceEquipment.includes(
                                equipment.title
                            );
                            return (
                                <motion.div
                                    key={index}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => handleSelectEquipment(equipment.title)}
                                    className={`cursor-pointer rounded-lg transition ${active
                                        ? "ring-2 ring-primary bg-primary/10 shadow-md"
                                        : "ring-2 ring-gray-100 hover:shadow-sm"
                                        }`}
                                >
                                    <Card className="h-full p-4 flex flex-col items-center justify-center text-center bg-transparent border-none">
                                        <img
                                            src={equipment.image}
                                            alt={equipment.title}
                                            className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
                                        />
                                        <CardContent className="p-2 font-medium">
                                            {equipment.title}
                                            {equipment.description && (
                                                <p className="text-sm text-gray-500">
                                                    {equipment.description}
                                                </p>
                                            )}
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                    {errors.equipment && (
                        <div className="text-red-500 text-sm">{errors.equipment}</div>
                    )}
                </div>

                {/* Info Note */}
                <div className="bg-gray-50 p-6 rounded-xl text-sm text-gray-700 leading-relaxed">
                    All equipment will be provided by the homeowner. Any specific
                    instructions should be provided below. (This information will not be
                    shared until the helper accepts the job)
                </div>

                {/* Instructions */}
                <div className="space-y-4">
                    <label className="flex items-center gap-2 text-lg font-medium">
                        <FileText className="w-5 h-5" />
                        Instructions
                    </label>
                    <Textarea
                        placeholder={getDefaultMessage()}
                        value={(jobPostProgress.selectedService.serviceInstruction || []).join("\n")}
                        onChange={(e) =>
                            setSelectedService({
                                service: jobPostProgress.selectedService.service,
                                serviceEquipment: jobPostProgress.selectedService.serviceEquipment,
                                serviceInstruction: e.target.value
                                    .split("\n")
                                    .filter((line) => line.trim() !== ""),
                            })
                        }
                        rows={6}
                        className="text-base placeholder:text-opacity-50"
                    />
                    {errors.instructions && (
                        <div className="text-red-500 text-sm">{errors.instructions}</div>
                    )}
                </div>

                {/* Footer Navigation */}
                <StepFooterNav
                    currentStep={currentStep}
                    totalSteps={totalSteps}
                    onBack={preStep}
                    onNext={handleNext}
                    nextDisabled={currentStep === totalSteps - 1}
                />

            </div>
        </div>
    );
}
